import { Paragraph, useHover } from "@ch/ui/pro";
import IconButton from "@ch/ui/pro/iconButton";
import Clipboard from "@react-native-clipboard/clipboard";
import { LegacyRef, useEffect, useRef, useState, RefObject } from "react";
import { Text, View } from "react-native";
import { useToast } from "react-native-toast-notifications";

import { tailwind } from "../styles";
import CarbonTooltip from "../ui/CarbonTooltip";

type WrappedTextProps = {
  text?: string;
  displayText?: string;
  copyButtonAccessibilityLabel?: string;
  noOfLines?: number;
  truncatedManually?: boolean;
  copyButton?: boolean;
  tooltipMaxWidth?: number;
};

const WrappedText = ({
  text,
  displayText,
  noOfLines = 1,
  copyButtonAccessibilityLabel,
  truncatedManually,
  copyButton,
  tooltipMaxWidth,
}: WrappedTextProps) => {
  const [ref, isHovered] = useHover();
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef<Text | null>(null);

  useEffect(() => {
    if (textRef.current && isHovered) {
      const element = textRef.current as unknown as HTMLElement;
      const isTextTruncated =
        truncatedManually ||
        element.scrollHeight > element.clientHeight ||
        element.scrollWidth > element.clientWidth;
      setIsTruncated(isTextTruncated);
    }
  }, [truncatedManually, isHovered]);
  const toast = useToast();

  if (!text)
    return (
      <Paragraph level={2} numberOfLines={1}>
        {text}
      </Paragraph>
    );

  const copyToClipboard = () => {
    Clipboard.setString(text);
    toast.show(`${text} copied to clipboard!`, { duration: 1250 });
  };

  const paragraph = (
    <Paragraph level={2} numberOfLines={noOfLines} ref={textRef as RefObject<Text>}>
      {displayText ?? text}
    </Paragraph>
  );

  return (
    <View ref={ref as LegacyRef<View>} style={tailwind("flex-row justify-between items-center")}>
      {isTruncated ? (
        <CarbonTooltip
          tooltipContent={text}
          tooltipContainerStyle={tooltipMaxWidth ? { maxWidth: tooltipMaxWidth } : undefined}
          triggerStyle={tailwind("flex-1")}
        >
          {paragraph}
        </CarbonTooltip>
      ) : (
        paragraph
      )}
      {copyButton && isHovered && (
        <IconButton
          size="sm"
          iconName="copy-small"
          onPress={copyToClipboard}
          type="transparent"
          accessibilityLabel={copyButtonAccessibilityLabel}
        />
      )}
    </View>
  );
};

export default WrappedText;
